import { useState } from "react";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { usePageTitle } from "../lib/usePageTitle";
import { toast } from "sonner";
import { Mail, Send, Sparkles, Check, Clock } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function ContactPage() {
  usePageTitle("Contact");
  const { user } = useAuth();
  const isAuthed = user && user !== false;

  const [name, setName] = useState(isAuthed ? (user.name || "") : "");
  const [email, setEmail] = useState(isAuthed ? (user.email || "") : "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async (e) => {
    e?.preventDefault();
    if (!name.trim() || !email.trim() || !subject.trim() || !message.trim()) {
      toast.error("Remplissez tous les champs");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/contact", {
        name: name.trim(),
        email: email.trim(),
        subject: subject.trim(),
        message: message.trim(),
      });
      setResult(data);
      setSubject("");
      setMessage("");
      toast.success(
        data.auto_sent
          ? "Réponse envoyée immédiatement par notre IA !"
          : "Message reçu — un humain va vous répondre sous 24-48h."
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'envoi");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 py-12" data-testid="contact-page">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-2xl bg-[var(--omni-accent)] text-white flex items-center justify-center">
            <Mail size={22} />
          </div>
          <div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Nous contacter</h1>
            <p className="text-xs text-gray-500">
              Une question, une suggestion, un bug ? Écrivez-nous — vous serez lu(e) en personne.
            </p>
          </div>
        </div>

        <div className="mt-6 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-900 flex gap-3" data-testid="contact-ai-notice">
          <Sparkles size={16} className="mt-0.5 flex-shrink-0" />
          <div>
            Une IA de support propulsée par <strong>Claude Sonnet 4.5</strong> lit votre message.
            Si la question est simple (FAQ, comment-faire), elle vous répond immédiatement par
            email. Sinon, votre demande arrive sur le bureau d'un humain qui vous écrira sous 24-48h.
          </div>
        </div>

        {result && (
          <div
            className={`mt-6 p-4 rounded-xl border flex items-start gap-3 ${
              result.auto_sent
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : "bg-blue-50 border-blue-200 text-blue-900"
            }`}
            data-testid="contact-result"
          >
            {result.auto_sent ? <Check size={18} className="mt-0.5" /> : <Clock size={18} className="mt-0.5" />}
            <div className="text-sm">
              {result.auto_sent ? (
                <>
                  <div className="font-bold">Réponse envoyée !</div>
                  <div>
                    Notre IA a jugé votre question simple et vient de vous répondre par email à{" "}
                    <code className="font-mono">{email}</code>. Vérifiez votre boîte de réception.
                  </div>
                </>
              ) : (
                <>
                  <div className="font-bold">Message bien reçu</div>
                  <div>
                    Votre demande nécessite une attention humaine. Un membre de l'équipe vous
                    répondra par email à <code className="font-mono">{email}</code> sous 24 à 48h.
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Nom</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Votre nom"
                data-testid="contact-name"
                required
              />
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Email</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="vous@exemple.com"
                data-testid="contact-email"
                required
              />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Sujet</label>
            <Input
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="De quoi s'agit-il ?"
              data-testid="contact-subject"
              required
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Message</label>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={8}
              placeholder="Décrivez votre demande avec autant de détails que possible…"
              data-testid="contact-message"
              required
            />
          </div>
          <div className="flex items-center gap-3">
            <Button
              type="submit"
              disabled={sending}
              className="rounded-full bg-[var(--omni-accent)] hover:bg-[var(--omni-accent-hover)] text-white"
              data-testid="contact-submit"
            >
              <Send size={14} className="mr-2" />
              {sending ? "Envoi…" : "Envoyer"}
            </Button>
            <span className="text-xs text-gray-400">
              Nous ne partageons jamais votre adresse avec des tiers.
            </span>
          </div>
        </form>
      </main>
      <Footer />
    </div>
  );
}
